import React, { useEffect } from 'react';
import {
    Modal,
    TextInput,
    NumberInput,
    Button,
    Stack,
    Flex,
    Alert,
} from '@mantine/core';
import { useForm } from '@mantine/form';
import { useMutation } from '@apollo/client';
import { notifications } from '@mantine/notifications';
import { IconAlertTriangle } from '@tabler/icons-react';
import {
    CREATE_COUNTRY_VAT_CONFIG,
    UPDATE_COUNTRY_VAT_CONFIG,
} from '../../apollo/mutations/vatConfig';
import { GET_COUNTRY_VAT_CONFIGS } from '../../apollo/queries/vatConfig';
import { CountryVatConfigRow } from './vatConfigTypes';

type Props = {
    opened: boolean;
    onClose: () => void;
    countryVatConfig?: CountryVatConfigRow;
};

type FormValues = {
    countryCode: string;
    countryName: string;
    vatPercent: number;
};

export function CountryVatConfigFormModal({
    opened,
    onClose,
    countryVatConfig,
}: Props) {
    const form = useForm<FormValues>({
        initialValues: {
            countryCode: countryVatConfig?.countryCode ?? '',
            countryName: countryVatConfig?.countryName ?? '',
            vatPercent: countryVatConfig?.vatPercent ?? 15,
        },
        validate: {
            countryCode: value =>
                value.trim().length < 2 ? 'Enter a valid country code' : null,
            countryName: value =>
                value.trim().length < 2 ? 'Enter a valid country name' : null,
            vatPercent: value =>
                value < 0 || value > 100 ? 'Must be between 0 and 100' : null,
        },
    });

    useEffect(() => {
        if (opened) {
            form.setValues({
                countryCode: countryVatConfig?.countryCode ?? '',
                countryName: countryVatConfig?.countryName ?? '',
                vatPercent: countryVatConfig?.vatPercent ?? 15,
            });
            form.clearErrors();
            form.resetDirty();
        }
    }, [countryVatConfig, opened]);

    const [createCountryVatConfig, { loading: creating, error: createError }] =
        useMutation(CREATE_COUNTRY_VAT_CONFIG, {
            refetchQueries: [{ query: GET_COUNTRY_VAT_CONFIGS }],
        });

    const [updateCountryVatConfig, { loading: updating, error: updateError }] =
        useMutation(UPDATE_COUNTRY_VAT_CONFIG, {
            refetchQueries: [{ query: GET_COUNTRY_VAT_CONFIGS }],
        });

    const handleClose = () => {
        form.reset();
        onClose();
    };

    const handleSubmit = async (values: FormValues) => {
        const input = {
            countryCode: values.countryCode.trim().toUpperCase(),
            countryName: values.countryName.trim(),
            vatPercent: values.vatPercent,
        };

        if (countryVatConfig) {
            const result = await updateCountryVatConfig({
                variables: { id: countryVatConfig.id, input },
            }).catch(() => null);
            if (!result?.data) {
                return;
            }
            notifications.show({
                title: 'Country VAT rate updated',
                message: `${input.countryName} is now set to ${input.vatPercent}%.`,
                color: 'green',
            });
        } else {
            const result = await createCountryVatConfig({
                variables: { input },
            }).catch(() => null);
            if (!result?.data) {
                return;
            }
            notifications.show({
                title: 'Country VAT rate added',
                message: `${input.countryName} has been set to ${input.vatPercent}%.`,
                color: 'green',
            });
        }

        handleClose();
    };

    const saving = creating || updating;
    const mutationError = createError ?? updateError;

    return (
        <Modal
            opened={opened}
            onClose={handleClose}
            title={
                countryVatConfig
                    ? 'Edit Country VAT Rate'
                    : 'Add Country VAT Rate'
            }
            size="md"
            centered
        >
            <form onSubmit={form.onSubmit(handleSubmit)}>
                <Stack gap="md">
                    <TextInput
                        label="Country Code"
                        description="ISO country code, e.g. ZA, BW"
                        placeholder="ZA"
                        maxLength={3}
                        required
                        {...form.getInputProps('countryCode')}
                        onChange={event =>
                            form.setFieldValue(
                                'countryCode',
                                event.currentTarget.value.toUpperCase(),
                            )
                        }
                    />
                    <TextInput
                        label="Country Name"
                        placeholder="South Africa"
                        required
                        {...form.getInputProps('countryName')}
                    />
                    <NumberInput
                        label="VAT %"
                        description="VAT rate applied to stores in this country (0-100)"
                        min={0}
                        max={100}
                        decimalScale={2}
                        required
                        {...form.getInputProps('vatPercent')}
                    />
                    {mutationError && (
                        <Alert
                            color="red"
                            icon={<IconAlertTriangle size={16} />}
                            title="Error saving country VAT rate"
                        >
                            {mutationError.message}
                        </Alert>
                    )}
                    <Flex justify="flex-end" gap="xs">
                        <Button
                            type="button"
                            variant="outline"
                            fullWidth={false}
                            onClick={handleClose}
                            disabled={saving}
                        >
                            Cancel
                        </Button>
                        <Button
                            type="submit"
                            fullWidth={false}
                            loading={saving}
                        >
                            {countryVatConfig ? 'Update' : 'Add'}
                        </Button>
                    </Flex>
                </Stack>
            </form>
        </Modal>
    );
}
